import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Subject, forkJoin, of, takeUntil, from } from 'rxjs';
import parsePhoneNumber from 'libphonenumber-js';
import * as moment from 'moment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from 'src/app/services/auth.service';
import { MetaService } from 'src/app/services/meta.service';
import { UserService } from 'src/app/services/user.service';
import { FacilityService } from 'src/app/services/facility.service';
import { CurrencyService } from 'src/app/services/currency.service';
import { AlertService } from 'src/app/services/alert.service';
import { CancelMembershipComponent } from 'src/app/components/popups/my-drop/cancel-membership/cancel-membership.component';
import { ConfirmDialogComponent } from 'src/app/components/popups/confirm-dialog/confirm-dialog.component';

import { ClassesTab, CurrencyMeta } from 'src/app/models/meta';
import { Utils } from 'src/app/services/common/utils';
import { Location, ResponseWrapper } from 'src/app/models/common-models';
import { Booking, MemberCurrency } from 'src/app/models/member';
import { CurrencyType, MembershipAction, MembershipType, TermUnit } from 'src/app/models/enums';
import { CancelMembershipRequest } from 'src/app/models/currency';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentService } from 'src/app/services/environment.service';

@Component({
  selector: 'df-my-drop',
  templateUrl: './my-drop.component.html',
  styleUrls: ['./my-drop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyDropComponent implements OnInit, OnDestroy {

  private readonly _unsubscribe: Subject<any> = new Subject();
  name: string = '';
  dob: string = '';
  phoneNumber: string = '';
  email: string = '';
  subscriptions: any[] = [];
  packs: any[] = [];
  location: Location = <Location>{};
  classesTab: ClassesTab = <ClassesTab>{};
  currencyMeta: CurrencyMeta = <CurrencyMeta>{};
  bookedClasses: any[] = [];
  isLoading = false;
  renewBookingId: number = 0;
  allowDeleteAccount: boolean = false;
  private hasPromptedRenewFromLink = false;

  // Dropdown state management
  activeDropdownId: number | null = null;

  constructor(
    private _user: UserService,
    private _auth: AuthService,
    private activeRoute: ActivatedRoute,
    private _metaService: MetaService,
    private facilityService: FacilityService,
    private currencyService: CurrencyService,
    private alertService: AlertService,
    private _environmentService: EnvironmentService,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.renewBookingId = Number(this.activeRoute.snapshot.queryParams['renewBookingId']) || 0;
    this.isLoading = true;
    this.allowDeleteAccount = this._environmentService.getFacilityConfig().allowDeleteAccount;

    this._loadData();
  }

  private _loadData(refreshMemberAccount = false): void {
    const member = this._auth.getProp('member');
    if (!member?.Id) {
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    forkJoin({
      memberAccount: this._user.fetchMemberAccount(refreshMemberAccount) || of(null),
      currencyMeta: from(this._metaService.getCurrencyMetaByMember(member.Id)),
    }).pipe(
      takeUntil(this._unsubscribe)
    ).subscribe({
      next: (results) => {
        try {
          const memberAccount = results.memberAccount;
          const bookedClasses = this.getUpcomingBookedClasses(memberAccount);

          if (bookedClasses.length === 0) {
            try {
              this._processData({
                memberAccount,
                currencyMeta: results.currencyMeta,
                classesMeta: {} as ClassesTab,
              });
            } finally {
              this.isLoading = false;
              this.cdr.markForCheck();
              this.promptRenewFromLink();
            }
            return;
          }

          this._metaService.getClassesMeta(false, member.LocationId).pipe(
            takeUntil(this._unsubscribe)
          ).subscribe({
            next: (classesMeta) => {
              try {
                this._processData({
                  memberAccount,
                  currencyMeta: results.currencyMeta,
                  classesMeta,
                });
              } finally {
                this.isLoading = false;
                this.cdr.markForCheck();
                this.promptRenewFromLink();
              }
            },
            error: (err: any) => {
              this.isLoading = false;
              console.error(err);
              this.cdr.markForCheck();
            }
          });
        } catch (err) {
          this.isLoading = false;
          console.error(err);
          this.cdr.markForCheck();
        }
      },
      error: (err: any) => {
        this.isLoading = false;
        console.error(err);
        this.cdr.markForCheck();
      }
    });
  }

  private getUpcomingBookedClasses(memberAccount: any): any[] {
    return (memberAccount?.Upcoming?.ClassBookings || [])
      .filter((cb: any) => cb && cb.MemberId === memberAccount?.Id && this.isInFuture(cb.StartDate, cb.StartTime));
  }

  private _processData(results: any): void {
    const memberAccount = results.memberAccount;

    // Process subscriptions and packs
    this.subscriptions = this._user.getMemberSubscriptions(memberAccount)
      .filter((s: Booking) => s.Description !== 'Pause Subscription');
    
    this.packs = this._user.getMemberPacks(memberAccount);
    this.location = memberAccount?.Location || <Location>{};
    
    // Process user data
    const user = this._user.getUser() || memberAccount?.Person || {};
    if (!this._user.getUser() && memberAccount?.Person) {
      this._auth.setProp('user', memberAccount.Person);
    }
    this.name = user.FullName || [user.FirstName, user.LastName].filter(Boolean).join(' ');
    this.dob = user.BirthDate ? moment(user.BirthDate).format('MM/DD/YYYY') : '';
    this.phoneNumber = user.PhoneNumber ? parsePhoneNumber((user.DialCode || '') + user.PhoneNumber)?.formatNational() || user.PhoneNumber : '';
    this.email = user.Email || '';
    
    // Process metadata
    this.classesTab = results.classesMeta;
    this.currencyMeta = results.currencyMeta;
    
    // Filter booked classes efficiently
    this.bookedClasses = this.getUpcomingBookedClasses(memberAccount);
  }

  ngOnDestroy(): void {
    this._unsubscribe.next(null);
    this._unsubscribe.complete();
  }

  isInFuture(startDate: string, startTime: string): boolean {
    var sessionStartTimestamp: number = Utils.getTimestampFromDateTimeUTC(new Date(startDate), startTime);
    const facilityTimestamp = this.facilityService.utcTime?.TimeStamp;
    if (facilityTimestamp == null) {
      return true;
    }
    if (sessionStartTimestamp < facilityTimestamp) {
      return false;
    }
    return true;
  }

  daysLeftSub(sub: Booking): string {
    if (this._user.isFreeTrial(sub) && !sub.IsStarted) {
      return 'Not started.';
    }
    
    const today = Date.now();
    const nextChargableDate = new Date(sub.NextChargableDate).getTime();

    if (
      sub.Cycles &&
      sub.Cycles.length > 0 &&
      sub.Cycles[0].StartAt &&
      new Date(sub.Cycles[0].StartAt).getTime() > today
    ) {
      return `Subscription starts from ${moment(sub.Cycles[0].StartAt).format('DD MMM YYYY')}.`;
    }
    
    const days = Math.floor((nextChargableDate - today) / (24 * 60 * 60 * 1000));
    
    let classesRemaining = '';
    if (sub.CurrencyId === 1) {
      classesRemaining = `${this._user.classesRemainingSub([sub], this.currencyMeta.Packs)} classes remaining.`;
    }
    
    return `${nextChargableDate > today ? 'Renews' : 'Ends'} in ${days} days. ${classesRemaining}`;
  }

  daysLeftPack(pack: MemberCurrency): number {
    const endAt = new Date(pack.EndAt).getTime();
    const today = Date.now();
    return Math.floor((endAt - today) / (24 * 60 * 60 * 1000));
  }

  renewAvailable(subscription: Booking) : boolean {
    if(subscription.Id == 0)
      return false;

    if (this.isCancelled(subscription))
      return false;

    // Cleared NCD (0001) after cancel-schedule must not unlock renew / Next cycle
    // (that compared as always-due and showed the furthest prepaid period).
    if (!this.isValidMembershipDate(subscription.NextChargableDate)) {
      return false;
    }

    if(this.renewBookingId == subscription.Id)
      return true;
    
    var checkDate : Date = Utils.nextDate(new Date(), 1, Utils.getPreviousUnit(subscription.TermUnit));
    if(checkDate >= new Date(subscription.NextChargableDate) && subscription.Action !== MembershipAction.CancelRequested && subscription.Action !== MembershipAction.ScheduledCancellation)
    {
      return true;
    }
    return false;
  }

  renew(subscription: Booking): void {
    if (confirm("Do you want to renew the subscription?") == true) {
      this.isLoading = true;
      this.currencyService.renew(subscription.MemberId, subscription.Id).subscribe((result: boolean | string) => {
        this.isLoading = false;
        if(typeof(result) === 'string')
        {
          this.alertService.error(result, true, true);
          window.location.href = '/account';
          return;
        } 
        else if(typeof(result) === 'object')
        {
          if(result == null)
          {
            this.alertService.error("Something went wrong. Please try again later.", true, true);
            window.location.href = '/account?renewBookingId=' + subscription.Id;
            return;
          }
          var result1 = result as ResponseWrapper;
          if(result1.StatusCode != 200)
          {
            this.alertService.error(result1.Message, true, true);
            window.location.href = '/account';
            return;
          } else {
            this.alertService.success('Renewed successfully.', true, true);
            window.location.href = '/account';
            return;
          }
        } else {
          this.alertService.success('Renewed successfully.', true, true);
          window.location.href = '/account';
          return;
        }
      });
    } 
    return;
  }

  private promptRenewFromLink(): void {
    if (!this.renewBookingId || this.hasPromptedRenewFromLink) {
      return;
    }

    const subscription = this.subscriptions.find((s: Booking) => s.Id === this.renewBookingId);
    if (!subscription) {
      return;
    }

    this.hasPromptedRenewFromLink = true;
    setTimeout(() => this.renew(subscription));
  }


  descriptionPack(p: any): string {
    return p.Comment; //`${p.Balance} class${p.Balance > 1 ? 'es' : ''}`;
  }

  /**
   * Next billing period from *today*, not the last prepaid member_currency row.
   * Renewals pre-create months ahead; getLastCycle() pointed at Jan 2027 while NCD was Sep 2026.
   * Facility cycle bounds are calendar DATE values — advance/format in UTC day units.
   */
  nextCycleDates(sub: Booking): string {
    const formatRange = (start: moment.Moment): string => {
      const end = start.clone();
      switch (sub.TermUnit) {
        case TermUnit.Day:
          end.add(sub.TermDuration, 'days');
          break;
        case TermUnit.Week:
          end.add(sub.TermDuration, 'weeks');
          break;
        case TermUnit.Month:
          end.add(sub.TermDuration, 'months');
          break;
        case TermUnit.Year:
          end.add(sub.TermDuration, 'years');
          break;
        default:
          end.add(sub.TermDuration, 'months');
          break;
      }
      return `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`;
    };

    const current = this._user.getCurrentCycle(sub);
    if (current && !current.IsCancelled) {
      return formatRange(moment.utc(current.EndAt).startOf('day'));
    }

    if (this.isValidMembershipDate(sub.NextChargableDate)) {
      return formatRange(moment.utc(sub.NextChargableDate).startOf('day'));
    }

    const upcoming = this._user.getUpcomingCycle(sub);
    if (upcoming && !upcoming.IsCancelled) {
      return `${moment.utc(upcoming.StartAt).format('DD MMM YYYY')} - ${moment.utc(upcoming.EndAt).format('DD MMM YYYY')}`;
    }

    return '';
  }

  packBalance(p: any) {
    if(p.CurrencyId == 8)
      return ``;
    var scale = this.currencyMeta.Currencies.find((c: any) => c.Id == p.CurrencyId)?.Scale;
    return `Expires in ${this.daysLeftPack(p)} days. Credits remaining: ${p.Balance / (scale || 1)}.`
  }

  private isValidMembershipDate(value?: string | null): boolean {
    return !!value && !String(value).startsWith('0001-01-01');
  }

  /**
   * Renews while another bill is still coming, including a scheduled cancel
   * whose commitment is after the next charge. That line stays Renews (shown
   * in red via subscriptionIsEnding) until the charge date passes, then
   * Expires on the commitment day.
   */
  getNextBillingDate(sub: Booking): string {
    if (this._user.isFreeTrial(sub) && !sub.IsStarted) {
      return '';
    }

    const day = this.periodEndDay(sub);
    if (!day) {
      return '';
    }

    const renews = !this.subscriptionIsEnding(sub) || this.renewsOnceMoreBeforeCommitment(sub);
    const label = renews ? 'Renews' : 'Expires';
    return `${label}: ${this.formatMembershipDay(day)}`;
  }

  /** Pending cancel still has a charge strictly before the commitment day. */
  private renewsOnceMoreBeforeCommitment(sub: Booking): boolean {
    if (!this.subscriptionIsEnding(sub)) {
      return false;
    }
    const ncd = this.membershipDay(sub.NextChargableDate);
    const commitment = this.membershipDay(sub.CommitmentEndDate);
    if (!ncd || !commitment || !commitment.isAfter(ncd, 'day')) {
      return false;
    }
    return !ncd.isBefore(moment.utc().startOf('day'), 'day');
  }

  subscriptionIsEnding(sub: Booking): boolean {
    return this.isCancelled(sub) || this.hasPendingCancellation(sub);
  }

  /** Commitment date. Omitted when the membership has no commitment. */
  getCommitmentDate(sub: Booking): string {
    if (!this.isValidMembershipDate(sub.CommitmentEndDate)) {
      return '';
    }
    return `Commitment: ${this.formatMembershipDay(moment.utc(sub.CommitmentEndDate))}`;
  }

  getCurrentCreditsLine(sub: Booking): string {
    return this.creditsLine('Credits', sub, this._user.getCurrentCycle(sub));
  }

  getNextCycleCreditsLine(sub: Booking): string {
    if (this.subscriptionIsEnding(sub)) {
      return '';
    }
    return this.creditsLine('Credits next cycle', sub, this.nextCreditCycle(sub));
  }

  private creditsLine(label: string, sub: Booking, cycle: any): string {
    if (!cycle || sub.Type === MembershipType.Addon || this.isPaused(sub)) {
      return '';
    }
    if (this._user.isFreeTrial(sub) && !sub.IsStarted) {
      return '';
    }
    if (this.isUnlimitedPack(cycle.PackId) || this.isUnlimitedPack(sub.PackId)) {
      return '';
    }
    if (this.isAccessMembership(sub.CurrencyId)) {
      return '';
    }

    if (cycle.Balance == null || cycle.Balance === '') {
      return '';
    }
    const balance = Number(cycle.Balance);
    if (!Number.isFinite(balance)) {
      return '';
    }

    const currency = this.currencyMeta?.Currencies?.find((c: any) => c.Id === sub.CurrencyId);
    const scale = currency?.Scale || 1;
    const amount = balance / scale;
    const formatted = Number.isInteger(amount) ? String(amount) : String(Math.round(amount * 10) / 10);
    return `${label}: ${formatted}`;
  }

  private isUnlimitedPack(packId?: number): boolean {
    if (!packId) {
      return false;
    }
    const pack = this.currencyMeta?.Packs?.find((p: any) => p.Id === packId);
    return !!pack?.IsUnlimited;
  }

  /** Gym and Cardio + Weights are floor access. They do not include class credits. */
  private isAccessMembership(currencyId?: number): boolean {
    const currency = this.currencyMeta?.Currencies?.find((c: any) => c.Id === currencyId);
    const unit = (currency?.ProductUnit || '').toString().toLowerCase();
    return unit === 'day';
  }

  private nextCreditCycle(sub: Booking): any | null {
    const current = this._user.getCurrentCycle(sub);
    const cycles = sub.Cycles || [];
    const currentEnd = current?.EndAt ? moment.utc(current.EndAt).startOf('day') : null;
    const today = moment.utc().startOf('day');
    let best: any = null;
    let bestStart = Number.POSITIVE_INFINITY;

    for (const cycle of cycles) {
      if (!cycle || cycle.IsCancelled) {
        continue;
      }
      if (current && (cycle === current || (cycle.Id && current.Id && cycle.Id === current.Id))) {
        continue;
      }
      const start = moment.utc(cycle.StartAt).startOf('day');
      if (!start.isValid()) {
        continue;
      }
      if (currentEnd) {
        if (start.isBefore(currentEnd, 'day')) {
          continue;
        }
      } else if (start.isBefore(today, 'day')) {
        continue;
      }
      const t = start.valueOf();
      if (t < bestStart) {
        bestStart = t;
        best = cycle;
      }
    }

    return best;
  }

  private periodEndDay(sub: Booking): moment.Moment | null {
    const ncd = this.membershipDay(sub.NextChargableDate);
    const commitment = this.membershipDay(sub.CommitmentEndDate);
    const today = moment.utc().startOf('day');

    if (this.subscriptionIsEnding(sub)) {
      if (commitment && ncd && commitment.isBefore(ncd, 'day')) {
        return commitment;
      }
      if (ncd && !ncd.isBefore(today, 'day')) {
        return ncd;
      }
      if (commitment && !commitment.isBefore(today, 'day')) {
        return commitment;
      }
      const cycleEnd = this.getCancelledAccessEndDate(sub);
      if (cycleEnd) {
        return cycleEnd;
      }
      return ncd || commitment;
    }

    return ncd;
  }

  private membershipDay(value?: string | null): moment.Moment | null {
    if (!this.isValidMembershipDate(value)) {
      return null;
    }
    const day = moment.utc(value).startOf('day');
    return day.isValid() ? day : null;
  }

  private formatMembershipDay(day: moment.Moment): string {
    const pattern = day.year() === moment().year() ? 'MMM D' : 'MMM D, YYYY';
    return day.format(pattern);
  }

  /** Last inclusive access day (do not use end-of-month). Prefer CommitmentEndDate;
   *  for Immediate/Cancelled (commitment cleared) fall back to current cycle EndAt. */
  getKeepMembershipThroughDate(subscription: Booking): string {
    if (this.isValidMembershipDate(subscription.CommitmentEndDate)) {
      return moment.utc(subscription.CommitmentEndDate).format('D MMM YYYY');
    }

    if (this.isCancelled(subscription)) {
      const cycleEnd = this.getCancelledAccessEndDate(subscription);
      if (cycleEnd) {
        return cycleEnd.format('D MMM YYYY');
      }
    }

    return '';
  }

  /** Inclusive last access day from the current cycle when commitment was cleared on cancel. */
  private getCancelledAccessEndDate(subscription: Booking): moment.Moment | null {
    const cycle = this.findInclusiveAccessCycle(subscription);
    if (!cycle) {
      return null;
    }
    const endRaw = (cycle as any).ExpireAt || cycle.EndAt;
    if (!endRaw || String(endRaw).startsWith('0001-01-01')) {
      return null;
    }
    const end = moment.utc(endRaw).startOf('day');
    if (!end.isValid()) {
      return null;
    }
    // Only while the paid period is still ongoing (today or future, UTC day).
    if (end.isBefore(moment.utc().startOf('day'))) {
      return null;
    }
    return end;
  }

  /**
   * Non-cancelled cycle whose UTC calendar range includes today (inclusive EndAt day).
   * Do not use UserService.getCurrentCycle — that compares timestamps and drops a
   * midnight EndAt later on the final access day.
   */
  private findInclusiveAccessCycle(subscription: Booking): any | null {
    if (!subscription?.Cycles?.length) {
      return null;
    }
    const today = moment.utc().startOf('day');
    for (const cycle of subscription.Cycles) {
      if (!cycle || cycle.IsCancelled) {
        continue;
      }
      const startRaw = cycle.StartAt;
      const endRaw = (cycle as any).ExpireAt || cycle.EndAt;
      if (!startRaw || !endRaw || String(endRaw).startsWith('0001-01-01')) {
        continue;
      }
      const start = moment.utc(startRaw).startOf('day');
      const end = moment.utc(endRaw).startOf('day');
      if (!start.isValid() || !end.isValid()) {
        continue;
      }
      if (!today.isBefore(start, 'day') && !today.isAfter(end, 'day')) {
        return cycle;
      }
    }
    return null;
  }

  /**
   * Expires for:
   * - class packages and personal training packages
   * - training subscriptions (PersonalTraining / GroupTraining currency types)
   * Never for class (Regular) subscriptions — billing/commitment cover that.
   */
  getExpiresDate(item: Booking | MemberCurrency): string {
    // Packs (MemberCurrency) — always show when a date exists
    if (!('Cycles' in item)) {
      const pack = item as MemberCurrency;
      const expireAt = (pack as any).ExpireAt || pack.EndAt;
      if (!expireAt) {
        return '';
      }
      return `Expires: ${this.formatMembershipDay(moment(expireAt))}`;
    }

    // Subscriptions — training currency types only; hide for class/Regular and others
    const sub = item as Booking;
    if (!this.isTrainingSubscription(sub)) {
      return '';
    }
    if (this._user.isFreeTrial(sub) && !sub.IsStarted) {
      return '';
    }

    const cycle = this._user.getCurrentCycle(sub);
    if (!cycle) {
      return '';
    }

    const expireAt = (cycle as any).ExpireAt || cycle.EndAt;
    if (!expireAt) {
      return '';
    }

    return `Expires ${moment(expireAt).format('D MMM YYYY')}.`;
  }

  /** Pack expiry turns red only inside the last 30 days. */
  packExpiresSoon(item: MemberCurrency): boolean {
    const expireAt = (item as any).ExpireAt || item.EndAt;
    if (!expireAt) {
      return false;
    }
    const day = moment(expireAt).startOf('day');
    if (!day.isValid()) {
      return false;
    }
    return day.diff(moment().startOf('day'), 'days') < 30;
  }

  /** True when the subscription's currency Type is a training variant (not Regular/class). */
  private isTrainingSubscription(sub: Booking): boolean {
    const currency = this.currencyMeta?.Currencies?.find((c: any) => c.Id === sub.CurrencyId);
    if (!currency) {
      return false;
    }
    switch (currency.Type) {
      case CurrencyType.PersonalTrainingFlexible:
      case CurrencyType.GroupTrainingFlexible:
      case CurrencyType.PersonalTrainingStrict:
      case CurrencyType.GroupTrainingStrict:
        return true;
      default:
        return false;
    }
  }

  private formatProductUnit(unit: string, plural: boolean): string {
    if (!unit) {
      return '';
    }
    let value = unit.charAt(0).toUpperCase() + unit.slice(1);
    if (plural) {
      if (unit.toLowerCase().endsWith('s')) {
        value += 'e';
      }
      value += 's';
    }
    return value;
  }

  getCreditsRemaining(item: Booking | MemberCurrency): string {
    // Handle packs (MemberCurrency)
    if ('EndAt' in item && !('Cycles' in item)) {
      const pack = item as MemberCurrency;
      
      // Skip if CurrencyId is 8 (based on packBalance logic)
      if (pack.CurrencyId === 8) {
        return '';
      }

      // Find the currency to get ProductUnit and Scale
      const currency = this.currencyMeta.Currencies.find((c: any) => c.Id === pack.CurrencyId);
      if (!currency) {
        return '';
      }

      const productUnitRaw = currency.ProductUnit || '';
      const scale = currency.Scale || 1;
      const balance = pack.Balance / scale;
      const productUnit = this.formatProductUnit(productUnitRaw, balance !== 1);

      return `Balance: ${balance} ${productUnit}.`;
    }

    // Handle subscriptions (Booking)
    const sub = item as Booking;
    if (this._user.isFreeTrial(sub) && !sub.IsStarted) {
      return '';
    }
    
    const cycle = this._user.getCurrentCycle(sub);
    if (!cycle) {
      return '';
    }

    // Check if subscription is Addon or Paused - don't show balance for these
    const isAddon = sub.Type === MembershipType.Addon;
    const isPaused = sub.Action === MembershipAction.Paused || sub.Action === MembershipAction.PauseInitiated;
    
    if (isAddon || isPaused) {
      return '';
    }

    // Find the pack to get scale
    const pack = this.currencyMeta.Packs.find((p: any) => p.Id === cycle.PackId);
    if (!pack) {
      return '';
    }

    // Find the currency to get ProductUnit
    const currency = this.currencyMeta.Currencies.find((c: any) => c.Id === sub.CurrencyId);
    const productUnitRaw = currency?.ProductUnit || '';

    if (pack.IsUnlimited) {
      return `Credits remaining: Unlimited ${this.formatProductUnit(productUnitRaw, true)}.`;
    }

    const scale = currency?.Scale || 1;
    const balance = parseInt(cycle.Balance, 10) || 0;
    const formattedBalance = balance / scale;
    const productUnit = this.formatProductUnit(productUnitRaw, formattedBalance !== 1);
    return `Credits remaining: ${formattedBalance} ${productUnit}.`;
  }




  // TrackBy functions for ngFor optimization
  trackBySubscription(index: number, subscription: any): any {
    return subscription.Id;
  }

  trackByPack(index: number, pack: any): any {
    return pack.Id;
  }

  trackByBookedClass(index: number, bookedClass: any): any {
    return bookedClass.Id;
  }

  // Dropdown methods
  toggleDropdown(subscriptionId: number): void {
    this.activeDropdownId = this.activeDropdownId === subscriptionId ? null : subscriptionId;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown')) {
      this.activeDropdownId = null;
      this.cdr.markForCheck();
    }
  }

  cancelSubscription(subscription: Booking): void {
    this.activeDropdownId = null;

    const modalRef = this.modalService.open(CancelMembershipComponent, {
      ariaLabelledBy: 'modal-basic-title',
      size: 'lg',
      centered: true,
      fullscreen: window.innerWidth <= 768
    });

    modalRef.componentInstance.memberId = subscription.MemberId;
    modalRef.componentInstance.bookingId = subscription.Id;
    modalRef.componentInstance.packTitle = this.getMenuPackName(subscription);

    modalRef.result.then((result) => {
      if (result) {
        this._loadData(true);
      }
    }).catch(() => {
      // Modal was dismissed
    });

    this.cdr.markForCheck();
  }

  /** Open cancellation request, including when the latest action is a payment update. */
  private hasPendingCancellation(subscription: Booking): boolean {
    return !!subscription.HasPendingCancellation
      || subscription.Action === MembershipAction.CancelRequested
      || subscription.Action === MembershipAction.ScheduledCancellation;
  }

  canPauseSubscription(subscription: Booking): boolean {
    if (this.isCancelled(subscription)) {
      return false;
    }

    const menuPacks = this.currencyMeta.Menus.flatMap((menu: any) => menu.MenuPacks || []);
    const menuPack = menuPacks.find((mp: any) => mp.Id === subscription.MenuPackId);

    return menuPack?.IsPausable === true
      && subscription.Action !== MembershipAction.Paused
      && subscription.Action !== MembershipAction.PauseInitiated
      && !this.hasPendingCancellation(subscription);
  }

  canResumeSubscription(subscription: Booking): boolean {
    if (this.isCancelled(subscription)) {
      return false;
    }

    return subscription.Action === MembershipAction.Paused || subscription.Action === MembershipAction.PauseInitiated;
  }

  /** Shown under the subscription details when the card failed or is updating. */
  getCardErrorMessage(subscription: Booking): string {
    if (subscription.Action === MembershipAction.PaymentFailed) {
      return 'Update payment card';
    }
    if (subscription.Action === MembershipAction.PaymentDetailsUpdated) {
      return 'Card update is in-progress.';
    }
    return '';
  }

  getMembershipTags(subscription: Booking): string[] {
    const tags: string[] = [];
    const cardError =
      subscription.Action === MembershipAction.PaymentFailed ||
      subscription.Action === MembershipAction.PaymentDetailsUpdated;
    if (cardError) {
      tags.push('Card error');
    } else if (subscription.IsPastDue) {
      tags.push('Past due');
    }
    if (subscription.Action === MembershipAction.Paused || subscription.Action === MembershipAction.PauseInitiated) {
      tags.push('Paused');
    }
    if (subscription.Type === MembershipType.Addon) {
      tags.push('Addon');
    }
    if (this.hasPendingCancellation(subscription) && !this.isCancelled(subscription)) {
      tags.push('Cancellation requested');
    }
    if (this.isCancelled(subscription)) {
      tags.push('Cancelled');
    }
    return tags;
  }

  isPaused(subscription: Booking): boolean {
    return subscription.Action === MembershipAction.Paused || subscription.Action === MembershipAction.PauseInitiated;
  }

  canCancelSubscription(subscription: Booking): boolean {
    if (this.isCancelled(subscription)) {
      return false;
    }

    if (this._user.isFreeTrial(subscription)) {
      return false;
    }

    return subscription.Type == MembershipType.Main && !this.hasPendingCancellation(subscription);
  }

  canKeepSubscription(subscription: Booking): boolean {
    if (this.isCancelled(subscription)) {
      return false;
    }

    return subscription.Type == MembershipType.Main && this.hasPendingCancellation(subscription);
  }

  hasDropdownOptions(subscription: Booking): boolean {
    return this.canPauseSubscription(subscription) ||
           this.canResumeSubscription(subscription) ||
           this.canCancelSubscription(subscription) ||
           this.canKeepSubscription(subscription);
  }

  /** Show keep-through while cancel is pending/scheduled, or Cancelled with remaining access. */
  isCancellationScheduled(subscription: Booking): boolean {
    if (this.hasPendingCancellation(subscription) && this.isValidMembershipDate(subscription.CommitmentEndDate)) {
      return true;
    }

    // Immediate/auto-approved cancel clears CommitmentEndDate but access continues through cycle end.
    if (this.isCancelled(subscription) && this.getKeepMembershipThroughDate(subscription)) {
      return true;
    }

    return false;
  }

  isCancelled(subscription: Booking): boolean {
    return subscription.Action === MembershipAction.Canceled;
  }


  pauseSubscription(subscription: Booking): void {
    this.activeDropdownId = null; // Close dropdown
    
    if (confirm("Are you sure you want to pause this subscription?")) {
      this._setLoading(true);

      this.currencyService.pauseSubscription(subscription.Id).subscribe({
        next: (result: number | string) => {
          this._setLoading(false);
          if (typeof(result) === 'string') {
            this.alertService.error(result, true, true);
            return;
          } else if (typeof(result) === 'object') {
            if (result == null) {
              this.alertService.error("Something went wrong. Please try again later.", true, true);
              return;
            }
            const result1 = result as ResponseWrapper;
            if (result1.StatusCode != 200) {
              this.alertService.error(result1.Message, true, true);
              return;
            } else {
              this.alertService.success('Subscription paused successfully.', true, true);
              this._loadData(true);
              return;
            }
          } else {
            this.alertService.success('Subscription paused successfully.', true, true);
            this._loadData(true);
            return;
          }
        },
        error: (err: any) => {
          this._setLoading(false);
          this.alertService.error(
            this._httpErrorUserMessage(err, "Failed to pause subscription. Please try again later."),
            true,
            true
          );
          console.error(err);
        }
      });
    }
    this.cdr.markForCheck();
  }

  resumeSubscription(subscription: Booking): void {
    this.activeDropdownId = null; // Close dropdown
    
    if (confirm("Are you sure you want to resume this subscription?")) {
      this._setLoading(true);

      this.currencyService.resumeSubscription(subscription.Id).subscribe({
        next: (result: number | string) => {
          this._setLoading(false);
          if (typeof(result) === 'string') {
            this.alertService.error(result, true, true);
            return;
          } else if (typeof(result) === 'object') {
            if (result == null) {
              this.alertService.error("Something went wrong. Please try again later.", true, true);
              return;
            }
            const result1 = result as ResponseWrapper;
            if (result1.StatusCode != 200) {
              this.alertService.error(result1.Message, true, true);
              return;
            } else {
              this.alertService.success('Subscription resumed successfully.', true, true);
              this._loadData(true);
              return;
            }
          } else {
            this.alertService.success('Subscription resumed successfully.', true, true);
            this._loadData(true);
            return;
          }
        },
        error: (err: any) => {
          this._setLoading(false);
          this.alertService.error("Failed to resume subscription. Please try again later.", true, true);
          console.error(err);
        }
      });
    }
    this.cdr.markForCheck();
  }

  cancelCancellationRequest(subscription: Booking): void {
    this.activeDropdownId = null;

    const modalRef = this.modalService.open(ConfirmDialogComponent, {
      ariaLabelledBy: 'keep-subscription-title',
      centered: true
    });

    modalRef.componentInstance.title = 'Keep Subscription';
    modalRef.componentInstance.message = 'Are you sure you want to withdraw your cancellation request? Your subscription will continue as normal.';
    modalRef.componentInstance.confirmLabel = 'Keep Subscription';
    modalRef.componentInstance.cancelLabel = 'Go Back';
    modalRef.componentInstance.confirmButtonClass = 'btn-primary';

    modalRef.result.then((confirmed) => {
      if (!confirmed) {
        return;
      }

      this._setLoading(true);

      const cancelRequest: CancelMembershipRequest = {
        MemberId: subscription.MemberId,
        BookingId: subscription.Id,
        ReasonCategory: '',
        Reason: ''
      };

      this.currencyService.cancelCancellationRequest(cancelRequest).subscribe({
        next: () => {
          this._setLoading(false);
          this.alertService.success('Cancellation request withdrawn. Your subscription will continue.', true, true);
          this._loadData(true);
        },
        error: (err: any) => {
          this._setLoading(false);
          this.alertService.error(
            this._httpErrorUserMessage(err, 'Failed to withdraw cancellation request. Please try again later.'),
            true,
            true
          );
          console.error(err);
        }
      });
    }).catch(() => {
      // Modal dismissed
    });

    this.cdr.markForCheck();
  }

  getMenuPackName(subscription: Booking): string {
    const menuPacks = this.currencyMeta.Menus?.flatMap((menu: any) => menu.MenuPacks || []) || [];
    const menuPack = menuPacks.find((mp: any) => mp.Id === subscription.MenuPackId);
    return menuPack?.Name || subscription.Description || 'membership';
  }

  // Private helper methods

  private _setLoading(loading: boolean): void {
    this.isLoading = loading;
    this.cdr.markForCheck();
  }

  private _httpErrorUserMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object') {
      const serverMsg = body.message ?? body.Message ?? body.detail ?? body.title;
      if (typeof serverMsg === 'string' && serverMsg.trim()) {
        return serverMsg;
      }
    }
    return fallback;
  }
}
